/**
 * Sessão de visualização pública (/reports/view/:id).
 *
 * Antes, "PIN validado" era só um booleano em sessionStorage e os dados vinham de endpoints
 * anônimos. Agora o PIN é trocado por um ViewToken assinado pelo backend, e é ele que autoriza
 * cada request em /public/view/*.
 *
 * O token fica em sessionStorage (morre ao fechar a aba) e nunca em localStorage: é credencial de
 * visitante, não de usuário, e não deve sobreviver à sessão do navegador.
 */

const TOKEN_PREFIX = "d4c:view_token:";
const SCOPE_PREFIX = "d4c:view_scope:";

/** Renova/pede PIN um pouco antes do vencimento real, para não falhar no meio de um carregamento. */
const EXPIRY_SKEW_MS = 30_000;

/**
 * Sessão ativa em memória. As requisições dos widgets nascem em componentes profundos
 * (ReportBuilderContext, DREWidget, ...) que não têm o id do relatório à mão, então o roteamento
 * para /public/view/* consulta este estado em vez de receber o id por parâmetro.
 */
let activeSession = null;

const tokenKey = (resourceId) => `${TOKEN_PREFIX}${resourceId}`;
const scopeKey = (resourceId) => `${SCOPE_PREFIX}${resourceId}`;

const readStoredSession = (resourceId) => {
  if (!resourceId) return null;

  try {
    const raw = sessionStorage.getItem(tokenKey(resourceId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.accessToken) return null;

    if (parsed.expiresAt && Date.parse(parsed.expiresAt) - EXPIRY_SKEW_MS <= Date.now()) {
      clearViewSession(resourceId);
      return null;
    }

    let scope = null;
    const rawScope = sessionStorage.getItem(scopeKey(resourceId));
    if (rawScope) {
      try {
        scope = JSON.parse(rawScope);
      } catch {
        scope = null;
      }
    }

    return { resourceId, accessToken: parsed.accessToken, expiresAt: parsed.expiresAt, scope };
  } catch {
    return null;
  }
};

/**
 * Reidrata a sessão a partir do sessionStorage (usado no mount do /view, em refresh de página).
 * Retorna a sessão ou null se não houver token utilizável.
 */
export const restoreViewSession = (resourceId) => {
  const stored = readStoredSession(resourceId);
  activeSession = stored;
  return stored;
};

/** Grava o resultado do handshake e ativa a sessão. */
export const startViewSession = (resourceId, { accessToken, expiresAt, scope }) => {
  if (!resourceId || !accessToken) return null;

  const session = { resourceId, accessToken, expiresAt: expiresAt || null, scope: scope || null };

  try {
    sessionStorage.setItem(
      tokenKey(resourceId),
      JSON.stringify({ accessToken, expiresAt: session.expiresAt }),
    );
    if (scope) sessionStorage.setItem(scopeKey(resourceId), JSON.stringify(scope));
  } catch (e) {
    // Modo privado / storage cheio: a sessão segue válida em memória durante a navegação atual.
    console.warn("Não foi possível persistir a sessão de visualização:", e);
  }

  activeSession = session;
  return session;
};

export const clearViewSession = (resourceId) => {
  const target = resourceId || activeSession?.resourceId;

  if (target) {
    try {
      sessionStorage.removeItem(tokenKey(target));
      sessionStorage.removeItem(scopeKey(target));
    } catch {
      /* storage indisponível */
    }
  }

  if (!resourceId || activeSession?.resourceId === resourceId) activeSession = null;
};

/** Sessão ativa, já validando expiração. */
export const getActiveViewSession = () => {
  if (!activeSession) return null;

  if (activeSession.expiresAt && Date.parse(activeSession.expiresAt) - EXPIRY_SKEW_MS <= Date.now()) {
    clearViewSession(activeSession.resourceId);
    return null;
  }

  return activeSession;
};

export const getViewAccessToken = () => getActiveViewSession()?.accessToken || null;

/**
 * Estamos operando como visitante público? É o que decide, nos services, entre a rota
 * autenticada e o equivalente em /public/view/*.
 */
export const isViewSessionActive = () => getActiveViewSession() !== null;

export const getViewScope = () => getActiveViewSession()?.scope || null;

/**
 * Evento disparado quando o backend recusa o ViewToken (expirado/revogado). O /view escuta para
 * reabrir o modal de PIN em vez de deixar a tela num estado quebrado.
 */
export const VIEW_SESSION_EXPIRED_EVENT = "d4c:view-session-expired";

export const notifyViewSessionExpired = () => {
  const resourceId = activeSession?.resourceId;
  clearViewSession(resourceId);
  window.dispatchEvent(new CustomEvent(VIEW_SESSION_EXPIRED_EVENT, { detail: { resourceId } }));
};
